/**
 * ReferenceLink Component
 * 
 * Displays clickable source links next to ChartMind items that have
 * matching clinical references. Clicking a link opens the PDF in an
 * in-app modal viewer instead of navigating to a new tab.
 * 
 * Each link shows the source name and the specific clinical tag that
 * caused the match, so the user can see exactly WHY the reference
 * is relevant to the current item.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import { MenuBook, Close as CloseIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { referenceSourceDisplayLabel } from '../../../../utils/parseDocumentTitle';

/**
 * Format the matched tag as a readable sub-name.
 * Capitalizes the first letter of each word for display.
 */
function formatMatchedTag(tag) {
  if (!tag) return '';
  return tag.replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Shows reference links for a matched item.
 * Each link displays: Source Name · Matched Tag
 * Clicking opens the PDF in a modal viewer.
 *
 * @param {{ references: Array<{
 *   source: string,
 *   downloadURL: string,
 *   documentType?: string,
 *   fileName?: string,
 *   matchedTag?: string,
 *   matchScore?: number,
 * }>, listLayout?: boolean }} props
 * @param listLayout - If true, show one link per line, sorted alphabetically by source (e.g. chatbot Source documents).
 * @param onOpenPdf - Optional. If provided, clicking a ref calls onOpenPdf(ref) and this component does not render its own PDF modal (caller provides one).
 */
const ReferenceLink = ({ references, listLayout = false, onOpenPdf }) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeRef, setActiveRef] = useState(null);
  const useExternalModal = typeof onOpenPdf === 'function';

  if (!references || references.length === 0) return null;

  const displayTitleFor = (ref) => referenceSourceDisplayLabel(ref) || 'Reference';

  const displayRefs = listLayout
    ? [...references].sort((a, b) =>
        displayTitleFor(a).localeCompare(displayTitleFor(b), undefined, { sensitivity: 'base' })
      )
    : references;

  const handleOpen = (ref, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (useExternalModal) {
      onOpenPdf(ref);
      return;
    }
    setActiveRef(ref);
    setViewerOpen(true);
  };

  /**
   * Close only the PDF viewer — fully isolate the event so it does NOT
   * propagate to (and close) any underlying popup/modal.
   */
  const handleClose = (event) => {
    // Stop the close event from reaching any parent (DiagnosisNodePopup, D3 canvas, etc.)
    if (event) {
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    }
    setViewerOpen(false);
    // Delay clearing activeRef so the dialog close animation finishes
    setTimeout(() => setActiveRef(null), 300);
  };

  const handleOpenExternal = (e) => {
    e.stopPropagation();
    if (activeRef?.downloadURL) {
      window.open(activeRef.downloadURL, '_blank', 'noopener,noreferrer');
    }
  };

  /**
   * Swallow all click and keyboard events inside the Dialog so they
   * never reach the DiagnosisNodePopup or D3 visualization underneath.
   */
  const isolateEvent = (e) => {
    e.stopPropagation();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: listLayout ? 'column' : 'row',
          alignItems: listLayout ? 'stretch' : 'center',
          gap: listLayout ? 0.25 : 0.5,
          flexWrap: listLayout ? 'nowrap' : 'wrap',
          ml: 0.5,
        }}
      >
        {displayRefs.map((ref, idx) => {
          const sourceName = displayTitleFor(ref);
          const matchedTag = ref.matchedTag ? formatMatchedTag(ref.matchedTag) : '';
          const tooltipText = [
            ref.documentType ? `${ref.documentType}:` : '',
            sourceName,
            ref.matchedTag ? `\nMatched: "${matchedTag}"` : '',
          ].filter(Boolean).join(' ');

          return (
            <Tooltip
              key={ref.id || idx}
              title={tooltipText}
              arrow
              placement="top"
            >
              <Typography
                component="span"
                variant="caption"
                onClick={(e) => handleOpen(ref, e)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.3,
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                  px: 0.5,
                  py: listLayout ? 0.25 : 0.1,
                  borderRadius: 0.5,
                  backgroundColor: 'action.hover',
                  cursor: 'pointer',
                  width: listLayout ? '100%' : undefined,
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <MenuBook sx={{ fontSize: '0.75rem' }} />
                {sourceName}
                {matchedTag && (
                  <Box
                    component="span"
                    sx={{
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      fontSize: '0.65rem',
                      ml: 0.2,
                    }}
                  >
                    · {matchedTag}
                  </Box>
                )}
              </Typography>
            </Tooltip>
          );
        })}
      </Box>

      {/* PDF Viewer Modal — only when not using external modal (e.g. chatbot provides its own) */}
      {!useExternalModal && (
      <Dialog
        open={viewerOpen}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        sx={{ zIndex: 10100 }}
        onClick={isolateEvent}
        onMouseDown={isolateEvent}
        onKeyDown={(e) => {
          // Capture Escape inside the dialog — close viewer only
          if (e.key === 'Escape') {
            e.stopPropagation();
            e.stopImmediatePropagation?.();
            handleClose(e);
          } else {
            e.stopPropagation();
          }
        }}
        // Prevent backdrop click from propagating to page
        BackdropProps={{
          onClick: (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation?.();
          },
          onMouseDown: (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation?.();
          },
        }}
        // Disable default Escape handling — we handle it in onKeyDown above
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: '1200px',
            m: 1,
          },
          onClick: isolateEvent,
          onMouseDown: isolateEvent,
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <MenuBook color="primary" sx={{ fontSize: '1.2rem', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                {activeRef ? displayTitleFor(activeRef) : 'Clinical Reference'}
              </Typography>
              {activeRef?.matchedTag && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  Matched: {formatMatchedTag(activeRef.matchedTag)}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Open in new tab" arrow>
              <IconButton size="small" onClick={handleOpenExternal}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleClose(e); }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {activeRef?.downloadURL ? (
            <iframe
              src={`${activeRef.downloadURL}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              title={activeRef ? displayTitleFor(activeRef) : 'PDF Viewer'}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                flex: 1,
              }}
            />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No document URL available.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      )}
    </>
  );
};

export default ReferenceLink;
